import * as React from 'react';
import { Routes, Route } from 'react-router-dom';

import loadable from '@common/react/loadable/loadableSettings';
import NotFoundRoute from '@common/react/components/Routes/NotFoundRoute';

import Layout from '@app/components/Layout';

import DashboardRoute from '@app/components/Routes/DashboardRoute';
import { MainWrapper } from '@app/components/Routes/MainRoute';
import OpenPageRoute from '@app/components/Routes/OpenPageRoute';

/* --------------Admin---------------*/

const Vitals = loadable(() => import(/* webpackChunkName: "VitalsPage" */ '@app/components/Pages/Admin/Vital/Vitals'));
const Inquiries = loadable(() => import(/* webpackChunkName: "InquiriesPage" */ '@app/components/Pages/Admin/Inquiries/Inquiries'));
const Sales = loadable(() => import(/* webpackChunkName: "SalesPage" */ '@app/components/Pages/Admin/Sales/Sales'));
const SaleEditor = loadable(() => import(/* webpackChunkName: "SalesEditorPage" */ '@app/components/Pages/Admin/Sales/SaleEditor'));
const Inquiry = loadable(() => import(/* webpackChunkName: "InquiryEditorPage" */ '@app/components/Pages/Admin/Inquiries/InquiryEditor'));
const Services = loadable(() => import(/* webpackChunkName: "ServicesPage" */ '@app/components/Pages/Admin/Services/Services'));
const Payers = loadable(() => import(/* webpackChunkName: "PayersPage" */ '@app/components/Pages/Admin/Payers/Payers'));
const Users = loadable(() => import(/* webpackChunkName: "UsersPage" */ '@app/components/Pages/Admin/Users/Users'));
const UserEditor = loadable(() => import(/* webpackChunkName: "UserEditorPage" */ '@app/components/Pages/Admin/Users/UserEditor'));
const SystemUserGroups = loadable(() => import(/* webpackChunkName: "SystemUserGroups" */ '@app/components/Pages/Admin/Users/SystemUserGroups'));
const Regions = loadable(() => import(/* webpackChunkName: "RegionsPage" */ '@app/components/Pages/Admin/Region/Regions'));
const RegionEditor = loadable(() => import(/* webpackChunkName: "RegionEditorPage" */ '@app/components/Pages/Admin/Region/RegionEditor'));
const Pages = loadable(() => import(/* webpackChunkName: "PagesPage" */ '@app/components/Pages/Admin/Pages/Pages'));
const PageEditor = loadable(() => import(/* webpackChunkName: "PageEditorPage" */ '@app/components/Pages/Admin/Pages/GeneralPageEditor'));
const ServicePageEditor = loadable(() =>
	import(/* webpackChunkName: "ServicePageEditorPage" */ '@app/components/Pages/Admin/Pages/ServicePageEditor'));
const Cases = loadable(() => import(/* webpackChunkName: "CasesPage" */ '@app/components/Pages/Admin/Cases/Cases'));
const CaseEditor = loadable(() => import(/* webpackChunkName: "CaseEditorPage" */ '@app/components/Pages/Admin/Cases/CaseEditor'));
const DocumentTypes = loadable(() => import(/* webpackChunkName: "DocumentTypesPage" */ '@app/components/Pages/Admin/DocumentTypes/DocumentTypes'));
const LedgerReport = loadable(() => import(/* webpackChunkName: "LedgerReport" */ '@app/components/Pages/Admin/Reports/Ledger/LedgerReport'));
const OrderDetailsReport = loadable(() =>
	import(/* webpackChunkName: "OrderDetailsReport" */ '@app/components/Pages/Admin/Reports/OrderDetails/OrderDetailsReport'));
const Orders = loadable(() => import(/* webpackChunkName: "OrdersPage" */ '@app/components/Pages/Admin/OrdersPage/OrdersPage'));
const Quotes = loadable(() => import(/* webpackChunkName: "Quotes" */ '@app/components/Pages/Admin/Quotes/Quotes'));
const Refunds = loadable(() => import(/* webpackChunkName: "Refunds" */ '@app/components/Pages/Admin/Refunds/Refunds'));
const Faqs = loadable(() => import(/* webpackChunkName: "FaqsPage" */ '@app/components/Pages/Admin/Faqs/Faqs'));
const ServiceCalls = loadable(() => import(/* webpackChunkName: "ServiceCallsPage" */ '@app/components/Pages/Admin/ServiceCall/ServiceCallsRoot'));
const SystemFaqs = loadable(() => import(/* webpackChunkName: "SystemFaqsPage" */ '@app/components/Pages/Admin/Faqs/SystemFaqs'));
const FaqEditor = loadable(() => import(/* webpackChunkName: "FaqEditorPage" */ '@app/components/Pages/Admin/Faqs/FaqEditor'));
const SystemFaqEditor = loadable(() => import(/* webpackChunkName: "SystemFaqEditorPage" */ '@app/components/Pages/Admin/Faqs/SystemFaqEditor'));
const ReviewRequests = loadable(() =>
	import(/* webpackChunkName: "ReviewRequestsPage" */ '@app/components/Pages/Admin/ReviewRequests/ReviewRequestsPage'));
const Reviews = loadable(() => import(/* webpackChunkName: "ReviewsPage" */ '@app/components/Pages/Admin/Reviews/Reviews'));
const ReviewEditor = loadable(() => import(/* webpackChunkName: "ReviewEditorPage" */ '@app/components/Pages/Admin/Reviews/ReviewEditor'));
const Locations = loadable(() => import(/* webpackChunkName: "LocationsPage" */ '@app/components/Pages/Admin/Locations/Locations'));
const LocationEditor = loadable(() => import(/* webpackChunkName: "LocationEditorPage" */ '@app/components/Pages/Admin/Locations/LocationEditor'));
const PortalHomeSpecials = loadable(() =>
	import(/* webpackChunkName: "PortalHomeSpecialsPage" */ '@app/components/Pages/Admin/Specials/PortalHomeSpecials'));
const PortalDoctors = loadable(() => import(/* webpackChunkName: "PortalDoctorsPage" */ '@app/components/Pages/Admin/Doctors/PortalDoctors'));
const Doctors = loadable(() => import(/* webpackChunkName: "DoctorsPage" */ '@app/components/Pages/Admin/Doctors/Doctors'));
const DoctorEditor = loadable(() => import(/* webpackChunkName: "DoctorEditorPage" */ '@app/components/Pages/Admin/Doctors/DoctorEditor'));
const Professions = loadable(() => import(/* webpackChunkName: "ProfessionsPage" */ '@app/components/Pages/Admin/Professions/Professions'));
const Honors = loadable(() => import(/* webpackChunkName: "HonorsPage" */ '@app/components/Pages/Admin/Honors/Honors'));
const HonorEditor = loadable(() => import(/* webpackChunkName: "HonorEditorPage" */ '@app/components/Pages/Admin/Honors/HonorEditor'));
const Dashboard = loadable(() => import(/* webpackChunkName: "DashboardPage" */ '@app/components/Pages/Admin/Dashboard/Dashboard'));
const Patients = loadable(() => import(/* webpackChunkName: "PatientsPage" */ '@app/components/Pages/Admin/Patients/Patients'));
const PatientEditor = loadable(() =>
	import(/* webpackChunkName: "PatientEditorPage" */ '@app/components/Pages/Admin/Patients/PatientEditorWithServer'));
const Appointments = loadable(() =>
	import(/* webpackChunkName: "AppointmentsPage" */ '@app/components/Pages/Admin/Appointments/AppointmentsWithServerPage'));
const AppointmentEditor = loadable(() =>
	import(/* webpackChunkName: "AppointmentEditorPage" */ '@app/components/Pages/Admin/Appointments/AppointmentEditorPage'));
const Analyzes = loadable(() => import(/* webpackChunkName: "AnalyzesPage" */ '@app/components/Pages/Admin/Analyzes/Analyzes'));
const AnalysisEditor = loadable(() =>
	import(/* webpackChunkName: "AnalysisEditorPage" */'@app/components/Pages/Admin/Analyzes/AnalysisEditor/AnalysisEditor'));
const InstagramPosts = loadable(() =>
	import(/* webpackChunkName: "InstagramPostsPage" */ '@app/components/Pages/Admin/InstagramPosts/InstagramPosts'));
const InstagramPostEditor = loadable(() =>
	import(/* webpackChunkName: "InstagramPostEditorPage" */ '@app/components/Pages/Admin/InstagramPosts/InstagramPostEditor'));
const AuthLinks = loadable(() => import(/* webpackChunkName: "AuthLinks" */ '@common/react/components/Pages/AuthLinks/AuthLinks'));
const AuthLinksFilters = loadable(() =>
	import(/* webpackChunkName: "AuthLinksFilters" */ '@commonTuna/react/components/Pages/Admin/AuthLinks/AuthLinksFilters'));
const ChatsPage = loadable(() => import(/* webpackChunkName: "ChatsPage" */ '@app/components/Pages/Admin/Chats/Chats'));
const SupportChatsPage = loadable(() => import(/* webpackChunkName: "SupportChatsPage" */ '@app/components/Pages/Admin/SupportChats/SupportChats'));
const AppointmentEmails = loadable(() =>
	import(/* webpackChunkName: "AppointmentEmailsPage" */ '@app/components/Pages/Admin/AppointmentEmails/AppointmentEmails'));
const Calendar = loadable(() => import(/* webpackChunkName: "CalendarPage" */ '@app/components/Pages/Admin/Calendar/CalendarWrapper'));
const Categories = loadable(() => import(/* webpackChunkName: "CategoriesPage" */ '@app/components/Pages/Admin/Categories/Categories'));
const CategoryEditor = loadable(() => import(/* webpackChunkName: "CategoryEditorPage" */ '@app/components/Pages/Admin/Categories/CategoryEditor'));
const Products = loadable(() => import(/* webpackChunkName: "ProductsPage" */ '@app/components/Pages/Admin/Products/Products'));
const ProductEditor = loadable(() => import(/* webpackChunkName: "ProductEditorPage" */ '@app/components/Pages/Admin/Products/ProductEditor'));
const Statuses = loadable(() => import(/* webpackChunkName: "StatusesPage" */ '@app/components/Pages/Admin/Statuses/Statuses'));
const Documents = loadable(() => import(/* webpackChunkName: "DocumentsPage" */ '@app/components/Pages/Admin/Documents/Documents'));
const Invoices = loadable(() => import(/* webpackChunkName: "InvoicesPage" */ '@app/components/Pages/Admin/Invoices/Invoices'));
const InvoiceEditor = loadable(() => import(/* webpackChunkName: "InvoiceEditorPage" */ '@app/components/Pages/Admin/Invoices/InvoiceEditor'));
const GlobalQuestions = loadable(() => import(/* webpackChunkName: "GlobalDiseasesPage" */ '@app/components/Pages/Admin/Diseases/GlobalQuestions'));
const MedicalQuestions = loadable(() => import(/* webpackChunkName: "DiseasesPage" */ '@app/components/Pages/Admin/Diseases/MedicalQuestions'));
const GlobalQuestionEditor = loadable(() =>
	import(/* webpackChunkName: "GlobalDiseaseEditorPage" */ '@app/components/Pages/Admin/Diseases/GlobalQuestionEditor'));
const MedicalQuestionEditor = loadable(() =>
	import(/* webpackChunkName: "DiseaseEditorPage" */ '@app/components/Pages/Admin/Diseases/MedicalQuestionEditor'));
const Templates = loadable(() => import(/* webpackChunkName: "TemplatesPage" */ '@app/components/Pages/Admin/Templates/Templates'));
const TemplateEditor = loadable(() => import(/* webpackChunkName: "TemplateEditorPage" */ '@app/components/Pages/Admin/Templates/TemplateEditor'));
const ResearchFormEditor = loadable(() =>
	import(/* webpackChunkName: "ResearchFormEditorPage" */ '@app/components/Pages/Admin/Templates/ResearchFormEditor'));
const EmailTemplates = loadable(() =>
	import(/* webpackChunkName: "EmailTemplatesPage" */ '@app/components/Pages/Admin/EmailTemplate/EmailTemplates'));
const Verifications = loadable(() => import(/* webpackChunkName: "VerificationsPage" */ '@app/components/Pages/Admin/Verification/Verifications'));
const SystemVerifications = loadable(() =>
	import(/* webpackChunkName: "SystemVerificationsPage" */ '@app/components/Pages/Admin/Verification/SystemVerifications'));
const UserCompanies = loadable(() =>
	import(/* webpackChunkName: "UserCompaniesPage" */ '@app/components/Pages/Admin/Users/UserCompanies'));
const EmailTemplateEditor = loadable(() =>
	import(/* webpackChunkName: "EmailTemplateEditorPage" */ '@app/components/Pages/Admin/EmailTemplate/EmailTemplateEditor'));
const Accounts = loadable(() => import(/* webpackChunkName: "AccountsPage" */ '@app/components/Pages/Admin/Accounts/Accounts'));
const CompanyEditor = loadable(() => import(/* webpackChunkName: "CompanyEditorPage" */ '@app/components/Pages/Admin/Company/CompanyEditor'));
const Ledgers = loadable(() => import(/* webpackChunkName: "LedgersPage" */ '@app/components/Pages/Admin/Ledgers/Ledgers'));
const LedgerEditor = loadable(() => import(/* webpackChunkName: "LedgerEditorPage" */ '@app/components/Pages/Admin/Ledgers/LedgerEditor'));
const SpecialsList = loadable(() => import(/* webpackChunkName: "SpecialsPage" */ '@app/components/Pages/Admin/Specials/Specials'));
const SpecialsEditor = loadable(() => import(/* webpackChunkName: "SpecialEditorPage" */ '@app/components/Pages/Admin/Specials/SpecialEditor'));
const AppointmentTypes = loadable(() =>
	import(/* webpackChunkName: "AppointmentTypesPage" */ '@app/components/Pages/Admin/AppointmentTypes/AppointmentTypes'));
const AddressBook = loadable(() => import(/* webpackChunkName: "AddressBookPage" */ '@app/components/Pages/Admin/AddressBook/AddressBook'));
const Tasks = loadable(() => import(/* webpackChunkName: "TasksPage" */ '@app/components/Pages/Admin/Tasks/Tasks'));
const TaskEditor = loadable(() => import(/* webpackChunkName: "TaskEditorPage" */ '@app/components/Pages/Admin/Tasks/TaskEditor'));
const ProcedureList = loadable(() => import(/* webpackChunkName: "ProceduresPage" */ '@app/components/Pages/Admin/Procedures/Procedures'));
const ProcedureEditor = loadable(() =>
	import(/* webpackChunkName: "ProcedureEditorPage" */ '@app/components/Pages/Admin/Procedures/ProcedureEditor/ProcedureEditor'));
const WorkShiftsReport = loadable(() => import(/* webpackChunkName: "WorkShiftsReport" */ '@app/components/Pages/Admin/WorkShifts/WorkShiftsReport'));
const RoleSchemas = loadable(() => import(/* webpackChunkName: "RoleSchemasPage" */ '@app/components/Pages/Admin/RoleSchemas/RoleSchemas'));
const DaysOffReport = loadable(() => import(/* webpackChunkName: "DaysOffReport" */ '@app/components/Pages/Admin/DaysOff/DaysOffReport'));
const BalanceReport = loadable(() =>
	import(/* webpackChunkName: "BalanceReport" */ '@app/components/Pages/Admin/Reports/Balance/BalanceReport'));
const AppointmentInsuranceList = loadable(() =>
	import(/* webpackChunkName: "InsurancesPage" */ '@app/components/Pages/Admin/Appointments/Insurance/AppointmentInsurances'));
const ClaimsPayersReport = loadable(() =>
	import(/* webpackChunkName: "ClaimsPayersReport" */ '@app/components/Pages/Admin/Reports/ClaimsPayers/ClaimPayersReport'));
const BatchList = loadable(() => import(/* webpackChunkName: "BatchesPage" */ '@app/components/Pages/Admin/Batches/Batches'));
const BatchEditor = loadable(() => import(/* webpackChunkName: "BatchEditorPage" */ '@app/components/Pages/Admin/Batches/BatchEditor/BatchEditor'));
const ProcedureGroupList = loadable(() =>
	import(/* webpackChunkName: "ProcedureGroupsPage" */ '@app/components/Pages/Admin/ProcedureGroups/ProcedureGroups'));
const FaxEventList = loadable(() =>
	import(/* webpackChunkName: "FaxEventsPage" */ '@app/components/Pages/Admin/FaxEvents/FaxEvents'));
const ClaimPaymentDocuments = loadable(() =>
	import(/* webpackChunkName: "PaymentDocuments" */ '@app/components/Pages/Admin/ClaimPaymentDocuments/ClaimPaymentDocuments'));
const ClaimReports = loadable(() => import(/* webpackChunkName: "ClaimReports" */ '@app/components/Pages/Admin/Reports/Claim/ClaimReports'));
const ClaimReportViewer = loadable(() =>
	import(/* webpackChunkName: "ClaimReportViewerPage" */'@app/components/Pages/Admin/Reports/Claim/ClaimReportViewer/ClaimReportViewer'));
const ClaimPayments = loadable(() => import(/* webpackChunkName: "ClaimPaymentsPage" */ '@app/components/Pages/Admin/ClaimPayments/ClaimPayments'));
const ClaimPaymentPage = loadable(() =>
	import(/* webpackChunkName: "ClaimPaymentPage" */ '@app/components/Pages/Admin/ClaimPayments/ClaimPaymentPage'));
const ServiceHours = loadable(() => import(/* webpackChunkName: "DoctorProceduresPage" */ '@app/components/Pages/Admin/Procedures/ServiceHours'));
const SyncTimes = loadable(() => import(/* webpackChunkName: "SyncTimesPage" */ '@commonTuna/react/components/Pages/Admin/SyncTimes/SyncTimes'));
const Wizard = loadable(() => import(/* webpackChunkName: "WizardPage" */ '@app/components/Pages/Admin/Wizard/Wizard'));
const AppointmentWizard = loadable(() =>
	import(/* webpackChunkName: "AppointmentWizardPage" */ '@app/components/Pages/Admin/AppointmentWizard/AppointmentWizard'));
const Checkout = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '@app/components/Pages/Admin/Checkout/Checkout'));
const CardPayment = loadable(() => import(/* webpackChunkName: "CardPaymentPage" */'@app/components/Pages/Admin/CardPayment/CardPayment'));
const UserSessions = loadable(() =>
	import(/* webpackChunkName: "UserSessionsPage" */ '@commonTuna/react/components/Pages/Admin/UserSessions/UserSessions'));
const Notifications = loadable(() => import(/* webpackChunkName: "NotificationsPage" */ '@app/components/Pages/Admin/Notifications/Notifications'));
const Prescriptions = loadable(() => import(/* webpackChunkName: "PrescriptionsPage" */ '@app/components/Pages/Admin/Prescriptions/Prescriptions'));
const Medications = loadable(() => import(/* webpackChunkName: "MedicationsPage" */ '@app/components/Pages/Admin/Medications/Medications'));
const AppointmentInsuranceEditor = loadable(() =>
	import(/* webpackChunkName: "InsuranceEditorPage" */ '@app/components/Pages/Admin/Appointments/Insurance/AppointmentInsuranceEditor'));
const GlobalPayersCompany = loadable(() =>
	import(/* webpackChunkName: "GlobalPayersCompanyPage" */ '@app/components/Pages/Admin/GlobalPayerCompany/GlobalPayersCompany'));
const ClaimPaymentDocumentDetails = loadable(() =>
	import(/* webpackChunkName: "ClaimPaymentDocumentPage" */ '@app/components/Pages/Admin/ClaimPaymentDocumentDetails/ClaimPaymentDocumentDetails'));
const ProfitReport = loadable(() => import(/* webpackChunkName: "RevenueReport" */ '@app/components/Pages/Admin/Reports/Profit/ProfitReport'));
const ProcedureReport = loadable(() =>
	import(/* webpackChunkName: "ProcedureReport" */ '@app/components/Pages/Admin/Reports/Procedure/ProcedureReport'));
const FaxRules = loadable(() => import(/* webpackChunkName: "FaxRulesPage" */ '@app/components/Pages/Admin/FaxRules/FaxRules'));
const TimeReport = loadable(() => import(/* webpackChunkName: "TimeReport" */ '@app/components/Pages/Admin/Reports/Time/TimeReport'));
const PatientReport = loadable(() =>
	import(/* webpackChunkName: "PatientReport" */ '@app/components/Pages/Admin/Reports/Patient/PatientReport'));
const NumbersReport = loadable(() =>
	import(/* webpackChunkName: "NumbersReport" */ '@app/components/Pages/Admin/Reports/Numbers/NumbersReport'));
const ProductReport = loadable(() =>
	import(/* webpackChunkName: "ProductReport" */ '@app/components/Pages/Admin/Reports/Product/ProductReport'));
const PaymentsReport = loadable(() =>
	import(/* webpackChunkName: "PaymentsReport" */ '@app/components/Pages/Admin/Reports/Payments/PaymentsReport'));
const IncomeStatementReport = loadable(() =>
	import(/* webpackChunkName: "IncomeStatementReport" */ '@app/components/Pages/Admin/Reports/IncomeStatement/IncomeStatementReport'));
const CompareReport = loadable(() =>
	import(/* webpackChunkName: "CompareReport" */ '@app/components/Pages/Admin/Reports/Compare/CompareReport'));
const ProvidersReport = loadable(() =>
	import(/* webpackChunkName: "ProviderReport" */ '@app/components/Pages/Admin/Reports/Providers/ProvidersReport'));
const SourceReport = loadable(() => import(/* webpackChunkName: "SourceReport" */ '@app/components/Pages/Admin/Reports/Source/SourceReport'));
const ProviderReport = loadable(() =>
	import(/* webpackChunkName: "ProviderReport" */ '@app/components/Pages/Admin/Reports/Provider/ProviderReport'));
const MetricsReport = loadable(() =>
	import(/* webpackChunkName: "MetricsReport" */ '@app/components/Pages/Admin/Reports/Metrics/MetricsReport'));
const ProductProvidersReport = loadable(() =>
	import(/* webpackChunkName: "UserReport" */ '@app/components/Pages/Admin/Reports/Providers/ProductProvidersReport'));
const ReviewRequestReport = loadable(() =>
	import(/* webpackChunkName: "ReviewRequestReport" */ '@app/components/Pages/Admin/Reports/ReviewRequest/ReviewRequestReport'));
const InquiriesReport = loadable(() =>
	import(/* webpackChunkName: "InquiriesReport" */ '@app/components/Pages/Admin/Reports/Inquiries/InquiriesReport'));
const Instructions = loadable(() => import(/* webpackChunkName: "InstructionsPage" */ '@app/components/Pages/Admin/Templates/Instructions'));
const ResearchForms = loadable(() => import(/* webpackChunkName: "ResearchFormsPage" */ '@app/components/Pages/Admin/Templates/ResearchForms'));
const Transactions = loadable(() => import(/* webpackChunkName: "TransactionsPage" */ '@app/components/Pages/Admin/Transactions/Transactions'));
const Terminals = loadable(() => import(/* webpackChunkName: "TerminalsPage" */ '@app/components/Pages/Admin/Terminals/Terminals'));
const TransactionViewer = loadable(() =>
	import(/* webpackChunkName: "TransactionViewerPage" */ '@app/components/Pages/Admin/Transactions/TransactionViewer'));
const CustomerProfileViewer = loadable(() =>
	import(/* webpackChunkName: "CustomerProfileViewerPage" */ '@app/components/Pages/Admin/Transactions/CustomerProfileViewer'));
const BillingPlanEditor = loadable(() =>
	import(/* webpackChunkName: "BillingPlanEditorPage" */ '@app/components/Pages/Admin/BillingPlans/BillingPlanEditor'));
const BillingPlans = loadable(() => import(/* webpackChunkName: "BillingPlansPage" */ '@app/components/Pages/Admin/BillingPlans/BillingPlans'));
const SpecialsReport = loadable(() => import(/* webpackChunkName: "SpecialsReport" */ '@app/components/Pages/Admin/Reports/Specials/SpecialsReport'));
const AppointmentTypesReport = loadable(() =>
	import(/* webpackChunkName: "AppointmentTypesReport" */ '@app/components/Pages/Admin/Reports/AppointmentTypes/AppointmentTypesReport'));
const GroupReport = loadable(() => import(/* webpackChunkName: "GroupReport" */ '@app/components/Pages/Admin/Reports/Group/GroupReport'));
const DayOffReport = loadable(() => import(/* webpackChunkName: "DayOffReport" */ '@app/components/Pages/Admin/Reports/DayOff/DayOffReport'));
const QuotasReport = loadable(() => import(/* webpackChunkName: "QuotasReport" */ '@app/components/Pages/Admin/Reports/Quotas/QuotasReport'));
const QuotesReport = loadable(() => import(/* webpackChunkName: "QuotesReport" */ '@app/components/Pages/Admin/Reports/Quotes/QuotesReport'));
const ItemsReport = loadable(() => import(/* webpackChunkName: "ItemsReport" */ '@app/components/Pages/Admin/Reports/Items/ItemsReport'));
const Preview = loadable(() => import(/* webpackChunkName: "PreviewPage" */ '@app/components/Pages/Admin/Preview/Preview'));
const CaseCreator = loadable(() => import(/* webpackChunkName: "CaseCreatorPage" */ '@app/components/Pages/Admin/Cases/CaseCreator'));
const PhoneCalls = loadable(() => import(/* webpackChunkName: "PhoneCallsPage" */ '@app/components/Pages/Admin/PhoneCalls/PhoneCalls'));
const PatientNotifications = loadable(() =>
	import(/* webpackChunkName: "PatientNotificationsPage" */ '@app/components/Pages/Admin/PatientNotifications/PatientNotifications'));
const BaseHostedServices = loadable(() =>
	import(/* webpackChunkName: "BaseHostedServicesPage" */ '@app/components/Pages/Admin/BaseHostedServices/BaseHostedServices'));
const PageAccesses = loadable(() => import(/* webpackChunkName: "PageAccessesPage" */ '@app/components/Pages/Admin/PageAccesses/PageAccesses'));
const SmsLogs = loadable(() => import(/* webpackChunkName: "SmsLogsPage" */ '@app/components/Pages/Admin/SmsLogs/SmsLogs'));
const EmailLogs = loadable(() => import(/* webpackChunkName: "EmailLogsPage" */ '@app/components/Pages/Admin/EmailLogs/EmailLogs'));
const PageImages = loadable(() => import(/* webpackChunkName: "PageImagesPage" */ '@app/components/Pages/Admin/PageImages/PageImages'));
const PageImageEditor = loadable(() =>
	import(/* webpackChunkName: "PageImageEditorPage" */ '@app/components/Pages/Admin/PageImages/PageImageEditor'));
const QuestionnaireHistory = loadable(() =>
	import(/* webpackChunkName: "QuestionnaireHistoryPage" */ '@app/components/Pages/Admin/QuestionnaireHistory/QuestionnaireHistory'));
const PaymentInquiries = loadable(() =>
	import(/* webpackChunkName: "PaymentInquiriesPage" */ '@app/components/Pages/Admin/PaymentInquiries/PaymentInquiries'));
const PaymentBatches = loadable(() =>
	import(/* webpackChunkName: "TransactionBatchesPage" */ '@app/components/Pages/Admin/PaymentBatches/PaymentBatches'));
const PaymentBatchEditor = loadable(() =>
	import(/* webpackChunkName: "PaymentBatchEditorPage" */ '@app/components/Pages/Admin/PaymentBatches/PaymentBatchEditor'));
const InventoriesReport = loadable(() =>
	import(/* webpackChunkName: "InventoriesReportPage" */ '@app/components/Pages/Admin/Reports/Inventories/InventoriesReport'));
const SubscriptionsPage = loadable(() =>
	import(/* webpackChunkName: "SubscriptionsPage" */ '@app/components/Pages/Admin/Subscription/Subscriptions'));
const Terms = loadable(() => import(/* webpackChunkName: "Terms" */ '@app/components/Pages/Admin/Terms/Terms'));
/* ------------Admin end-------------*/

const Login = loadable(() => import(/* webpackChunkName: "LoginPage" */ '@app/components/Pages/Login/Login'));
const Recover = loadable(() => import(/* webpackChunkName: "RecoverPage" */ '@app/components/Pages/Recover/Recover'));
const Confirm = loadable(() => import(/* webpackChunkName: "ConfirmPage" */ '@app/components/Pages/Confirm/Confirm'));
const Confirmation = loadable(() => import(/* webpackChunkName: "ConfirmationPage" */ '@app/components/Pages/Confirmation/Confirmation'));
const InstagramConfirmation = loadable(() =>
	import(/* webpackChunkName: "InstagramConfirmationPage" */ '@app/components/Pages/Admin/InstagramAccount/InstagramConfirmation'));
const Register = loadable(() => import(/* webpackChunkName: "RegisterPage" */ '@app/components/Pages/Register/Register'));
const Profile = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '@app/components/Pages/Profile'));
const PrivacyPolicy = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '@app/components/Pages/PrivacyPolicy/PrivacyPolicy'));
const PatientUnsubscribe = loadable(() => import(/* webpackChunkName: "UnsubscribePage" */ '@app/components/Pages/PatientUnsubscribe'));
const UserUnsubscribe = loadable(() => import(/* webpackChunkName: "UserUnsubscribePage" */ '@app/components/Pages/UserUnsubscribe'));

const AccessDenied = loadable(() => import(/* webpackChunkName: "AccessDenied" */ '@common/react/components/Pages/AccessDenied/AccessDenied'));
const ChangeEmail = loadable(() => import(/* webpackChunkName: "ChangeEmail" */ '@app/components/Pages/ChangeEmail/ChangeEmail'));
const ExpiredLink = loadable(() => import(/* webpackChunkName: "ExpiredLink" */ '@app/components/Pages/ExpiredLink/ExpiredLink'));

export const DashboardRoutes = [
	{
		path: '/calendar',
		component: Calendar,
		title: 'Calendar',
	},
	{
		path: '/profile',
		component: Profile,
		title: 'Profile',
	},
	{
		path: '/inquiry-list/:page?',
		component: Inquiries,
		title: 'Inquiries',
	},
	{
		path: '/sale-list/:page?',
		component: Sales,
		title: 'Sales',
	},
	{
		path: '/sale-editor/:id?',
		component: SaleEditor,
	},
	{
		path: '/vitals/:page?',
		component: Vitals,
		title: 'Vitals',
	},
	{
		path: '/inquiry-editor/:id',
		component: Inquiry,
	},
	{
		path: '/service-list/:page?',
		component: Services,
		title: 'Services',
	},
	{
		path: '/user-list/:page?',
		component: Users,
		title: 'Users',
	},
	{
		path: '/user-editor/:id',
		component: UserEditor,
	},
	{
		path: '/user-groups/:page?',
		component: SystemUserGroups,
		title: 'System User Groups',
	},
	{
		path: '/region-list/:page?',
		component: Regions,
		title: 'Regions',
	},
	{
		path: '/region-editor/:id?',
		component: RegionEditor,
		title: 'Regions',
	},
	{
		path: '/page-editor/:id',
		component: PageEditor,
	},
	{
		path: '/service-page-editor/:id',
		component: ServicePageEditor,
	},
	{
		path: '/page-list/:page?',
		component: Pages,
		title: 'Pages',
	},
	{
		path: '/case-list/:page?',
		component: Cases,
		title: 'Before-Afters',
	},
	{
		path: '/case-editor/:id',
		component: CaseEditor,
	},
	{
		path: '/documentType-list/:page?',
		component: DocumentTypes,
		title: 'Document Types',
	},
	{
		path: '/ledger-report',
		component: LedgerReport,
		title: 'Ledger',
	},
	{
		path: '/order-details/:page?',
		component: OrderDetailsReport,
		title: 'Order Details',
	},
	{
		path: '/order-list/:page?',
		component: Orders,
		title: 'Orders',
	},
	{
		path: '/quote-list/:page?',
		component: Quotes,
		title: 'Quotes',
	},
	{
		path: '/refund-list/:page?',
		component: Refunds,
		title: 'Refunds',
	},
	{
		path: '/faq-list/:page?',
		component: Faqs,
		title: 'Faqs',
	},
	{
		path: '/serviceCall-list/:page?',
		component: ServiceCalls,
		title: 'Service Calls',
	},
	{
		path: '/system-faq-list/:page?',
		component: SystemFaqs,
		title: 'System FAQs',
	},
	{
		path: '/faq-editor/:id',
		component: FaqEditor,
	},
	{
		path: '/system-faq-editor/:id',
		component: SystemFaqEditor,
	},
	{
		path: '/review-requests/:page?',
		component: ReviewRequests,
		title: 'Review Requests',
	},
	{
		path: '/review-list/:page?',
		component: Reviews,
		title: 'Reviews',
	},
	{
		path: '/review-editor/:id',
		component: ReviewEditor,
		title: 'ReviewEditor',
	},
	{
		path: '/portal-specials/:page?',
		component: PortalHomeSpecials,
		title: 'Portal Specials',
	},
	{
		path: '/portal-doctors/:page?',
		component: PortalDoctors,
		title: 'Portal Doctors',
	},
	{
		path: '/doctor-list/:page?',
		component: Doctors,
		title: 'Doctors',
	},
	{
		path: '/insurance-editor/:id',
		component: AppointmentInsuranceEditor,
		title: 'Appointment Insurance',
	},
	{
		path: '/doctor-editor/:id',
		component: DoctorEditor,
	},
	{
		path: '/location-list/:page?',
		component: Locations,
		title: 'Locations',
	},
	{
		path: '/location-editor/:id',
		component: LocationEditor,
	},
	{
		path: '/profession-list/:page?',
		component: Professions,
		title: 'Job Categories',
	},
	{
		path: '/honor-list/:page?',
		component: Honors,
		title: 'Awards Page',
	},
	{
		path: '/honor-editor/:id',
		component: HonorEditor,
	},
	{
		path: '/dashboard',
		component: Dashboard,
		title: 'Dashboard',
	},
	{
		path: '/patient-list/:page?',
		component: Patients,
		title: 'Patients',
	},
	{
		path: '/patient-editor/:id',
		component: PatientEditor,
	},
	{
		path: '/appointment-editor/:id',
		component: AppointmentEditor,
	},
	{
		path: '/appointment-list/:page?',
		component: Appointments,
		title: 'Appointments',
	},
	{
		path: '/analysis-list/:page?',
		component: Analyzes,
		title: 'Lab. Orders',
	},
	{
		path: '/analysis-editor/:id',
		component: AnalysisEditor,
	},
	{
		path: '/instagram-post-list/:page?',
		component: InstagramPosts,
		title: 'Instagram Posts',
	},
	{
		path: '/instagram-post-editor/:id',
		component: InstagramPostEditor,
	},
	{
		path: '/email-list/:page?',
		component: AppointmentEmails,
		title: 'Emails',
	},
	{
		path: '/chats',
		component: ChatsPage,
		title: 'Chats',
	},
	{
		path: '/support-chats',
		component: SupportChatsPage,
		title: 'Support Chats',
	},
	{
		path: '/categories/:page?',
		component: Categories,
		title: 'Categories',
	},
	{
		path: '/category-editor/:id',
		component: CategoryEditor,
	},
	{
		path: '/products/:page?',
		component: Products,
		title: 'Products',
	},
	{
		path: '/product-editor/:id',
		component: ProductEditor,
	},
	{
		path: '/status-list/:page?',
		component: Statuses,
		title: 'Statuses',
	},
	{
		path: '/document-list/:page?',
		component: Documents,
		title: 'Documents',
	},
	{
		path: '/prescription-list/:page?',
		component: Prescriptions,
		title: 'E-Rx',
	},
	{
		path: '/medications/:page?',
		component: Medications,
		title: 'Medications',
	},
	{
		path: '/invoice-list/:page?',
		component: Invoices,
		title: 'Invoices',
	},
	{
		path: '/invoice-editor/:id',
		component: InvoiceEditor,
	},
	{
		path: '/global-question-list/:page?',
		component: GlobalQuestions,
		title: 'Global Questions',
	},
	{
		path: '/global-question-editor/:id',
		component: GlobalQuestionEditor,
	},
	{
		path: '/medical-question-list/:page?',
		component: MedicalQuestions,
		title: 'Medical Questions',
	},
	{
		path: '/medical-question-editor/:id',
		component: MedicalQuestionEditor,
	},
	{
		path: '/template-list/:page?',
		component: Templates,
		title: 'Consent Forms',
	},
	{
		path: '/instruction-list/:page?',
		component: Instructions,
		title: 'Instruction Forms',
	},
	{
		path: '/research-forms-list/:page?',
		component: ResearchForms,
		title: 'Research Forms',
	},
	{
		path: '/template-editor/:id',
		component: TemplateEditor,
	},
	{
		path: '/instruction-editor/:id',
		component: TemplateEditor,
	},
	{
		path: '/research-forms-editor/:id',
		component: ResearchFormEditor,
	},
	{
		path: '/email-template/:page?',
		component: EmailTemplates,
		title: 'Email Templates',
	},
	{
		path: '/email-template-editor/:id',
		component: EmailTemplateEditor,
	},
	{
		path: '/account-list/:page?',
		component: Accounts,
		title: 'Accounts',
	},
	{
		path: '/company-editor',
		component: CompanyEditor,
	},
	{
		path: '/ledger-list/:page?',
		component: Ledgers,
		title: 'Billing',
	},
	{
		path: '/ledger-editor/:id',
		component: LedgerEditor,
	},
	{
		path: '/verifications-list/:page?',
		component: Verifications,
		title: 'Verifications',
	},
	{
		path: '/system-verifications-list/:page?',
		component: SystemVerifications,
		title: 'Verifications',
	},
	{
		path: '/user-companies/:page?',
		component: UserCompanies,
		title: 'User Companies',
	},
	{
		path: '/specials-list/:page?',
		component: SpecialsList,
		title: 'Specials',
	},
	{
		path: '/specials-editor/:id',
		component: SpecialsEditor,
	},
	{
		path: '/procedure-list/:page?',
		component: ProcedureList,
		title: 'Services',
	},
	{
		path: '/procedure-editor/:id',
		component: ProcedureEditor,
	},
	{
		path: '/service-hours/:page?',
		component: ServiceHours,
		title: 'Service Hours',
	},
	{
		path: '/payer-list/:page?',
		component: Payers,
		title: 'Payers',
	},
	{
		path: '/appointment-type-list/:page?',
		component: AppointmentTypes,
		title: 'Appointment Types',
	},
	{
		path: '/contact-list/:page?',
		component: AddressBook,
		title: 'Address Book',
	},
	{
		path: '/task-list/:page?',
		component: Tasks,
		title: 'Tasks',
	},
	{
		path: '/task-editor/:id',
		component: TaskEditor,
	},
	{
		path: '/work-shift-list/:page?',
		component: WorkShiftsReport,
		title: 'Work Shifts',
	},
	{
		path: '/roleschema-list/:page?',
		component: RoleSchemas,
		title: 'Access Level Schemas',
	},
	{
		path: '/notifications/:page?',
		component: Notifications,
		title: 'Notifications',
	},
	{
		path: '/day-off-list/:page?',
		component: DaysOffReport,
		title: 'Calendar Events',
	},
	{
		path: '/balance-check-report/:page?',
		component: BalanceReport,
		title: 'Balance Check Report',
	},
	{
		path: '/insurance-list/:page?',
		component: AppointmentInsuranceList,
		title: 'Claims',
	},
	{
		path: '/batch-list/:page?',
		component: BatchList,
		title: 'Batches',
	},
	{
		path: '/batch-editor/:id',
		component: BatchEditor,
		title: 'Batch',
	},
	{
		path: '/claim-payers-report/:page?',
		component: ClaimsPayersReport,
		title: 'Payers Report',
	},
	{
		path: '/procedure-group-list/:page?',
		component: ProcedureGroupList,
		title: 'Service Groups',
	},
	{
		path: '/fax-list/:page?',
		component: FaxEventList,
		title: 'Faxes',
	},
	{
		path: '/claim-payment-documents/:page?',
		component: ClaimPaymentDocuments,
		title: 'Checks',
	},
	{
		path: '/claim-payment-documents-details/:id',
		component: ClaimPaymentDocumentDetails,
		title: 'Check Details',
	},
	{
		path: '/claim-reports/:page?',
		component: ClaimReports,
		title: 'Claim Reports',
	},
	{
		path: '/claim-report/:id',
		component: ClaimReportViewer,
		title: 'Claim Report',
	},
	{
		path: '/claim-payments/:page?',
		component: ClaimPayments,
		title: 'Claim Payments',
	},
	{
		path: '/claim-payment/:id',
		component: ClaimPaymentPage,
		title: 'Claim Payment',
	},
	{
		path: '/global-payer-company-list/:page?',
		component: GlobalPayersCompany,
		title: 'Global Payers',
	},
	{
		path: '/revenue-report',
		component: ProfitReport,
		title: 'Procedure/Provider Report',
	},
	{
		path: '/procedure-report',
		component: ProcedureReport,
		title: 'Procedure Report',
	},
	{
		path: '/time-report',
		component: TimeReport,
		title: 'Apt. Duration Report',
	},
	{
		path: '/fax-rule-list/:page?',
		component: FaxRules,
		title: 'Fax Rules',
	},
	{
		path: '/numbers-report',
		component: NumbersReport,
		title: 'Numbers Report',
	},
	{
		path: '/product-report',
		component: ProductReport,
		title: 'Product Report',
	},
	{
		path: '/payments-report',
		component: PaymentsReport,
		title: 'Payments Report',
	},
	{
		path: '/income-report',
		component: IncomeStatementReport,
		title: 'Revenue Report',
	},
	{
		path: '/review-request-report',
		component: ReviewRequestReport,
		title: 'Review Request Report',
	},
	{
		path: '/compare-report',
		component: CompareReport,
		title: 'Comparison Report',
	},
	{
		path: '/providers-report',
		component: ProvidersReport,
		title: 'Providers Report',
	},
	{
		path: '/source-report',
		component: SourceReport,
		title: 'Source Report',
	},
	{
		path: '/provider-report',
		component: ProviderReport,
		title: 'Time Report',
	},
	{
		path: '/metrics-report',
		component: MetricsReport,
		title: 'Metrics Report',
	},
	{
		path: '/items-report',
		component: ItemsReport,
		title: 'Items Report',
	},
	{
		path: '/inventories-report',
		component: InventoriesReport,
		title: 'Inventory Report',
	},
	{
		path: '/user-report',
		component: ProductProvidersReport,
		title: 'Product/Provider',
	},
	{
		path: '/inquiries-report',
		component: InquiriesReport,
		title: 'Inquiries Report',
	},
	{
		path: '/transaction-list/:page?',
		component: Transactions,
		title: 'Transactions',
	},
	{
		path: '/transaction-editor/:id',
		component: TransactionViewer,
		title: 'Transaction',
	},
	{
		path: '/terminal-list/:page?',
		component: Terminals,
		title: 'Terminals',
	},
	{
		path: '/customer-profile/:id',
		component: CustomerProfileViewer,
		title: 'Customer Profile',
	},
	{
		path: '/billing-plan-editor/:id',
		component: BillingPlanEditor,
		title: 'Billing Plan Editor',
	},
	{
		path: '/billing-plan-list/:page?',
		component: BillingPlans,
		title: 'Billing Plans',
	},
	{
		path: '/specials-report',
		component: SpecialsReport,
		title: 'Specials Report',
	},
	{
		path: '/appointment-types-report',
		component: AppointmentTypesReport,
		title: 'Appointment Types Report',
	},
	{
		path: '/group-report',
		component: GroupReport,
		title: 'Sales Trends Report',
	},
	{
		path: '/dayoff-report/:page?',
		component: DayOffReport,
		title: 'Time OFF Report',
	},
	{
		path: '/quotas-report',
		component: QuotasReport,
		title: 'Orders Time Report',
	},
	{
		path: '/quotes-report',
		component: QuotesReport,
		title: 'Quotes Report',
	},
	{
		path: '/patients-report',
		component: PatientReport,
		title: 'Patients Report',
	},
	{
		path: '/phone-call-list/:page?',
		component: PhoneCalls,
		title: 'Phone Calls',
	},
	{
		path: '/patient-notifications',
		component: PatientNotifications,
		title: 'Patient Notifications',
	},
	{
		path: '/base-hosted-service-list/:page?',
		component: BaseHostedServices,
		title: 'Hosted Services',
	},
	{
		path: '/page-access-list/:page?',
		component: PageAccesses,
		title: 'Page Accesses',
	},
	{
		path: '/smsLogs',
		component: SmsLogs,
		title: 'Sms Logs',
	},
	{
		path: '/emailLogs',
		component: EmailLogs,
		title: 'Email Logs',
	},
	{
		path: '/page-image-list/:page?',
		component: PageImages,
		title: 'Page Images',
	},
	{
		path: '/page-image-editor/:id',
		component: PageImageEditor,
	},
	{
		path: '/questionnaire-history',
		component: QuestionnaireHistory,
		title: 'Questions History',
	},
	{
		path: '/payment-inquiries/:page?',
		component: PaymentInquiries,
		title: 'Payment Inquiries',
	},
	{
		path: '/sync-time-list/:page?',
		component: SyncTimes,
		title: 'Sync Info',
	},
	{
		path: '/terms/:page?',
		component: Terms,
		title: 'Terms',
	},
	{
		path: '/subscriptions/:page?',
		component: SubscriptionsPage,
		title: 'Subscriptions',
	},
	{
		path: '/payment-batch-editor/:id',
		component: PaymentBatchEditor,
		title: 'Payment Batch Editor',
	},
	{
		path: '/payment-batches/:page?',
		component: PaymentBatches,
		title: 'Payment Batches',
	},
	{
		path: '/authLink-list/:page?',
		component: (props) => <AuthLinks filters={<AuthLinksFilters />} />,
		title: 'Auth Links',
	},
	{
		path: '/user-session-list/:page?',
		component: () => <UserSessions withServerPage popoverClassName="dashboard-filters-component" />,
		title: 'Entry Log Journal',
	},
	{
		path: '/wizard',
		component: Wizard,
		dashboard: false,
	},
	{
		path: '/appointment-wizard/:id',
		component: AppointmentWizard,
		dashboard: false,
	},
	{
		path: '/checkout/:id',
		component: Checkout,
		dashboard: false,
	},
	{
		path: '/card-payment/:orderId',
		component: CardPayment,
		dashboard: false,
	},
	{
		path: '/preview-creator/:id',
		component: Preview,
		dashboard: false,
	},
	{
		path: '/case-creator/:id',
		component: CaseCreator,
		dashboard: false,
	},
];

export const baseRoutes = (
	<>
		<Route path="/" element={<MainWrapper />}>
			<Route path="/" element={<Login />} />
			<Route path="login" element={<Login />} />
			<Route path="registration" element={<Register />} />
		</Route>
		<Route path="/" element={<OpenPageRoute />}>
			<Route path="/recover" element={<Recover />} />
			<Route path="/confirm" element={<Confirm />} />
			<Route path="/confirmation" element={<Confirmation />} />
			<Route path="/instagram-confirmation" element={<InstagramConfirmation />} />
			<Route path="/change-email" element={<ChangeEmail />} />
			<Route path="/unsubscribe-patient" element={<PatientUnsubscribe />} />
			<Route path="/unsubscribe-user" element={<UserUnsubscribe />} />
			<Route path="/expiredLink" element={<ExpiredLink />} />
			<Route path="/privacy-policy" element={<PrivacyPolicy />} />
		</Route>
		<Route path="/">
			{DashboardRoutes.map((item) => <Route
				key={item.path}
				path={item.path}
				element={<DashboardRoute dashboard={item.dashboard ?? true} component={item.component} path={item.path} title={item.title} />}
			/>)}
		</Route>
		<Route
			path="/access-denied"
			element={<NotFoundRoute
				title="403 - ACCESS DENIED"
				component={AccessDenied}
				loginRoute={DashboardRoute}
				openRoute={(props) => <OpenPageRoute {...props} withHeader />}
			/>}
		/>
		<Route
			path="*"
			element={<NotFoundRoute
				loginRoute={DashboardRoute}
				openRoute={(props) => <OpenPageRoute {...props} withHeader />}
			/>}
		/>
	</>
);

export const clientRoutes = (<Route element={<Layout />}>
	{baseRoutes}
</Route>);

export const routes = (
	<Layout>
		<Routes>
			{baseRoutes}
		</Routes>
	</Layout>
);
